/**
 * i18n runtime — per-locale chunked loading.
 *
 * Bundle strategy:
 *  - en ships eagerly (small, the canonical fallback)
 *  - other locales are dynamic-imported on demand via virtual:i18n-<tag>
 *
 * Lookup falls back to en if a key is missing in the active locale.
 * Plurals via ICU-lite: {n, plural, =0 {…} one {…} other {…}}.
 */
import enDict from 'virtual:i18n-en';

export type Locale = 'en' | 'ja' | 'zh-Hant' | 'zh-Hans';
const LAUNCH_LOCALES: Locale[] = ['en', 'ja', 'zh-Hant', 'zh-Hans'];

interface I18nDict {
  [k: string]: string | I18nDict;
}

const fallbackDict: I18nDict = enDict as I18nDict;
const loaded: Partial<Record<Locale, I18nDict>> = { en: fallbackDict };

let activeLocale: Locale = 'en';
let activeDict: I18nDict = fallbackDict;

async function loadLocale(tag: Locale): Promise<I18nDict> {
  if (loaded[tag]) return loaded[tag]!;
  try {
    let mod: { default: unknown };
    if (tag === 'ja') mod = await import('virtual:i18n-ja');
    else if (tag === 'zh-Hant') mod = await import('virtual:i18n-zh-Hant');
    else if (tag === 'zh-Hans') mod = await import('virtual:i18n-zh-Hans');
    else return fallbackDict;
    const dict = mod.default as I18nDict;
    loaded[tag] = dict;
    return dict;
  } catch (err) {
    console.warn('i18n load failed', tag, err);
    return fallbackDict;
  }
}

export async function initI18n(navLang: string): Promise<I18nDict> {
  const want = resolveLocale(navLang);
  await setLocale(want);
  return activeDict;
}

export async function setLocale(locale: Locale): Promise<void> {
  activeLocale = locale;
  activeDict = await loadLocale(locale);
  document.documentElement.lang = locale;
}

/** Synchronous variant for tests that pre-load the locale. */
export function setLocaleSync(locale: Locale, dict?: I18nDict): void {
  activeLocale = locale;
  if (dict) loaded[locale] = dict;
  activeDict = loaded[locale] ?? fallbackDict;
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

export function getLocale(): Locale {
  return activeLocale;
}

function resolveLocale(navLang: string): Locale {
  const lower = navLang.toLowerCase();
  if (lower.startsWith('ja')) return 'ja';
  if (lower.startsWith('zh-tw') || lower.startsWith('zh-hk') || lower === 'zh-hant')
    return 'zh-Hant';
  if (lower.startsWith('zh')) return 'zh-Hans';
  return 'en';
}

export function isLaunchLocale(l: string): l is Locale {
  return (LAUNCH_LOCALES as readonly string[]).includes(l);
}

/**
 * t — translate by dotted key path. Falls back to en, then key.
 *
 * Variables: pass via `vars`. Pluralization uses ICU-style:
 *   "{n, plural, one {1 day} other {# days}}"
 */
export function t(key: string, vars: Record<string, unknown> = {}): string {
  const raw = lookup(activeDict, key) ?? lookup(fallbackDict, key) ?? key;
  return interpolate(raw, vars);
}

function lookup(dict: I18nDict, key: string): string | null {
  const parts = key.split('.');
  let cur: string | I18nDict | undefined = dict;
  for (const part of parts) {
    if (typeof cur !== 'object' || cur === null) return null;
    const next: string | I18nDict | undefined = (cur as I18nDict)[part];
    if (next === undefined) return null;
    cur = next;
  }
  return typeof cur === 'string' ? cur : null;
}

function interpolate(template: string, vars: Record<string, unknown>): string {
  let out = expandPlurals(template, vars);
  out = out.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = vars[k];
    if (v == null) return '';
    if (typeof v === 'number') return fmtNum(v);
    return String(v);
  });
  return out;
}

function expandPlurals(template: string, vars: Record<string, unknown>): string {
  let out = '';
  let i = 0;
  while (i < template.length) {
    const open = template.indexOf('{', i);
    if (open < 0) {
      out += template.slice(i);
      break;
    }
    out += template.slice(i, open);
    const close = findMatchingBrace(template, open);
    if (close < 0) {
      out += template.slice(open);
      break;
    }
    const inner = template.slice(open + 1, close);
    const replaced = tryPlural(inner, vars);
    out += replaced === null ? `{${inner}}` : replaced;
    i = close + 1;
  }
  return out;
}

function findMatchingBrace(s: string, openIdx: number): number {
  let depth = 0;
  for (let i = openIdx; i < s.length; i++) {
    const c = s[i];
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function tryPlural(inner: string, vars: Record<string, unknown>): string | null {
  const head = inner.match(/^\s*(\w+)\s*,\s*plural\s*,\s*/);
  if (!head) return null;
  const varName = head[1]!;
  const body = inner.slice(head[0].length);
  const arms = parsePluralArms(body);
  const n = Number(vars[varName] ?? 0);
  const ruleKey = pluralKey(n, activeLocale);
  const explicit = arms[`=${n}`];
  const branch = explicit ?? arms[ruleKey] ?? arms['other'] ?? '';
  return branch.replace(/#/g, fmtNum(n));
}

function parsePluralArms(body: string): Record<string, string> {
  const out: Record<string, string> = {};
  let i = 0;
  while (i < body.length) {
    while (i < body.length && /\s/.test(body[i]!)) i++;
    const keyMatch = body.slice(i).match(/^(=\d+|\w+)/);
    if (!keyMatch) break;
    const key = keyMatch[1]!;
    i += key.length;
    while (i < body.length && /\s/.test(body[i]!)) i++;
    if (body[i] !== '{') break;
    const close = findMatchingBrace(body, i);
    if (close < 0) break;
    out[key] = body.slice(i + 1, close);
    i = close + 1;
  }
  return out;
}

function pluralKey(n: number, locale: Locale): 'zero' | 'one' | 'two' | 'few' | 'many' | 'other' {
  if (typeof Intl !== 'undefined' && Intl.PluralRules) {
    return new Intl.PluralRules(locale).select(n) as
      | 'zero'
      | 'one'
      | 'two'
      | 'few'
      | 'many'
      | 'other';
  }
  return n === 1 ? 'one' : 'other';
}

export function fmtNum(n: number): string {
  return new Intl.NumberFormat(activeLocale).format(n);
}

export function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function fmtDate(d: Date | string, opts: Intl.DateTimeFormatOptions = {}): string {
  const date = d instanceof Date ? d : new Date(d);
  return new Intl.DateTimeFormat(activeLocale, opts).format(date);
}

export function fmtCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat(activeLocale, { style: 'currency', currency }).format(amount);
}

export function regionName(code: string): string {
  if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
    return new Intl.DisplayNames(activeLocale, { type: 'region' }).of(code) ?? code;
  }
  return code;
}
